import { useState } from "react";
import UserService from "../services/userService";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import LoadindForButton from '../components/LoadingForButton';
import { FaLock } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import NotificationBanner from "./NotificationBanner";
import AuthSideVisual from "./AuthSideVisual";

const ResetPassword = () => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({ code: '', password: '' });
    const [errors, setErrors] = useState({});
    const [invalidCredentials, setInvalidCredentials] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");

    const [passwordConditions, setPasswordConditions] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
    });

    const checkPasswordConditions = (value) => {
        const conditions = {
            length: value.length >= 8,
            uppercase: /[A-Z]/.test(value),
            lowercase: /[a-z]/.test(value),
            number: /[0-9]/.test(value),
        };
        setPasswordConditions(conditions);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors({ ...errors, [name]: '' });
        setFormData({ ...formData, [name]: value });
        if (name === 'password') checkPasswordConditions(value);
    };

    const toggleShowPassword = () => setShowPassword(!showPassword);

    const validate = () => {
        const newErrors = {};
        if (!formData.code) newErrors.code = t('codeRequired');
        else if (formData.code.length !== 5) newErrors.code = t('invalidVerificationCode');
        if (!formData.password) {
            newErrors.password = t('passwordRequired');
        } else {
            if (formData.password.length < 8)
                newErrors.password = t('passwordTooShort');
            if (formData.password.length > 50)
                newErrors.password = t('maxLengthError', { count: 50 });
            if (!/[A-Z]/.test(formData.password))
                newErrors.password = t('passwordUppercase');
            if (!/[a-z]/.test(formData.password))
                newErrors.password = t('passwordLowercase');
            if (!/[0-9]/.test(formData.password))
                newErrors.password = t('passwordNumber');
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            setIsSubmitted(true);
            return;
        }

        UserService.resetPassword({ code: formData.code, password: formData.password })
            .then((res) => {
                if (res.status === 200) {
                    setNotificationMessage(t('newPasswordSet'));
                    setShowNotification(true);
                    setLoading(false);
                    setTimeout(() => setShowNotification(false), 3000);
                    setTimeout(() => window.location.href = "/login", 3000);
                } else {
                    setInvalidCredentials(t('invalidCredentials'));
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (err.response?.data?.message === "New password cannot be the same as the old password.") {
                    setInvalidCredentials(t('passwordCannotBeSame'));
                } else {
                    setInvalidCredentials(t('invalidCredentials'));
                }
                setLoading(false);
            });
    };

    return (
        <div className="py-7 lg:px-4 flex flex-col lg:flex-row items-center justify-center h-full">
            {showNotification && <NotificationBanner message={notificationMessage} onClose={() => setShowNotification(false)} />}
            <div className="w-full lg:w-1/2 max-w-screen-md mx-auto">
                <div className="flex justify-center">
                    <img src="/logo2.png" className="w-12 h-12 lg:w-32 lg:h-32" alt="Weddinfy" />
                </div>
                <form onSubmit={handleSubmit} className="p-4 w-full max-w-screen-md" >
                    <h2 className="text-3xl font-bold my-2">{t('resetPasswordTitle')}</h2>
                    <span className="block mb-4">{t('resetPasswordSubtitle')}</span>
                    <div className="relative">
                        <label>{t('code')}</label>
                        <FaLock className='inputWeddingIcon' />
                        <input
                            type="text"
                            name="code"
                            maxLength={7}
                            value={formData.code}
                            onChange={handleChange}
                            className={`inputWedding ${errors.code ? '!border-red-500' : 'inputFieldNormal'}`}
                        />
                    </div>
                    {errors.code && <p className="text-red-500 text-sm">{errors.code}</p>}

                    <div className="relative mt-4">
                        <label>{t('newPassword')}</label>
                        <FaLock className='inputWeddingIcon' />
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            maxLength={50}
                            value={formData.password}
                            onChange={handleChange}
                            className={`inputWedding ${errors.password ? '!border-red-500' : 'inputFieldNormal'}`}
                        />
                        <button type="button" onClick={toggleShowPassword} className="absolute bottom-3 right-4">
                            {showPassword ? <IoEyeOutline size={20} /> : <IoEyeOffOutline size={20} />}
                        </button>
                    </div>
                    {!formData.password && <p className="text-red-500 text-sm">{errors.password}</p>}

                    {isSubmitted && formData.password && (
                        <div className="text-sm mt-2 text-gray-600">
                            <ul>
                                <li className={passwordConditions.length ? 'text-green-500' : 'text-red-500'}>
                                    {passwordConditions.length ? '✔' : '✖'} {t('passwordConditionLength')}
                                </li>
                                <li className={passwordConditions.uppercase ? 'text-green-500' : 'text-red-500'}>
                                    {passwordConditions.uppercase ? '✔' : '✖'} {t('passwordConditionUppercase')}
                                </li>
                                <li className={passwordConditions.lowercase ? 'text-green-500' : 'text-red-500'}>
                                    {passwordConditions.lowercase ? '✔' : '✖'} {t('passwordConditionLowercase')}
                                </li>
                                <li className={passwordConditions.number ? 'text-green-500' : 'text-red-500'}>
                                    {passwordConditions.number ? '✔' : '✖'} {t('passwordConditionNumber')}
                                </li>
                            </ul>
                        </div>
                    )}

                    {invalidCredentials && <p className="text-red-500 text-sm bg-red-100 rounded-lg p-2 mt-2">{invalidCredentials}</p>}

                    <button type="submit" className="submitButton w-full mt-7" disabled={loading}>
                        {loading ? <LoadindForButton /> : t('resetPasswordButton')}
                    </button>
                </form>
            </div>

            <div className="hidden lg:flex w-1/2 flex-col items-center justify-center text-center px-8 py-12 space-y-6">
                {/* <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center">
                    <FaChair className="text-white text-4xl" />
                </div>
                <Link to="/">
                    <h1 className="text-3xl font-extrabold text-gray-800">{t('appName')}</h1>
                </Link> */}
                <AuthSideVisual />
                <p className="text-gray-700 max-w-md">{t('slogan')}</p>
            </div>
        </div>
    );
};

export default ResetPassword;
